// ABOUTME: Smart wrapper for the Overbooking Allowance entity — owns useOverbookingAllowanceEntity and reports state via context.
// ABOUTME: Renders skeleton/error/form; computes UoM and reference event options from catalog.
import React, {useMemo} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useOverbookingAllowanceEntity} from '../hooks/useOverbookingAllowanceEntity'
import {useEntityFormContext, useRegisterEntityForm} from '../context/EntityFormContext'
import {EntityContentError} from './EntityContentError'
import {EntityContentSkeleton} from './EntityContentSkeleton'
import {OverbookingAllowanceForm} from './OverbookingAllowanceForm'

export const OverbookingAllowanceEntityForm: React.FC = () => {
  const {t} = useTranslation()
  const ctx = useEntityFormContext()
  const {config, isLoading, error, isSaving, actions} = useOverbookingAllowanceEntity({
    countryId: ctx.countryId,
    businessLine: ctx.businessLine,
    scopeId: ctx.selectedScopeId,
    pendingScope: ctx.pendingScope,
    rootScopeId: ctx.rootScopeId,
  })

  useRegisterEntityForm(actions, isSaving)

  const uomOptions = useMemo(() => {
    const fromBackend = (ctx.overbookingAllowanceOptions?.availableUnitsOfMeasure ?? [])
      .find((ref) => ref.id === 'businessDay')
    return [{
      value: 'businessDay',
      label: fromBackend
        ? (fromBackend.name || fromBackend.displaySymbol)
        : t('countryConfigurations.overbookingAllowance.uom.businessDay'),
    }]
  }, [ctx.overbookingAllowanceOptions, t])

  const referenceEventOptions = useMemo(
    () =>
      (ctx.overbookingAllowanceOptions?.availableReferenceEvents ?? []).map((evt) => ({
        value: evt,
        label: t(`countryConfigurations.overbookingAllowance.referenceEvents.${evt}`),
      })),
    [ctx.overbookingAllowanceOptions, t],
  )

  const effectiveConfig = useMemo(
    () =>
      config ?? {
        id: undefined,
        entries: [],
        overrides: {entries: false},
        parentValues: {entries: []},
        _meta: {
          isInherited: false,
          scopeId: ctx.selectedScopeId ?? '',
          resolvedFrom: null,
          createdAt: '',
          updatedAt: '',
          createdBy: '',
          updatedBy: '',
        },
      },
    [config, ctx.selectedScopeId],
  )

  if (isLoading) return <EntityContentSkeleton />
  if (error) return <EntityContentError />

  return (
    <OverbookingAllowanceForm
      key={ctx.formResetKey}
      config={effectiveConfig}
      isReadOnly={ctx.isReadOnly}
      onDirtyChange={ctx.onDirtyChange}
      onValidChange={ctx.onValidChange}
      onValuesChange={actions.handleValuesChange}
      uomOptions={uomOptions}
      referenceEventOptions={referenceEventOptions}
      isRoot={ctx.isCountryDefault}
    />
  )
}
